/**
 * Admin Math Lab — live documentation + interactive calculator for the canonical
 * math core (`website/utils/betMath.js`, generated from `functions/betMath.js`).
 *
 * The calculator is NOT a demonstration reimplementation. It calls the exact
 * functions production calls, so a number that is right here is right
 * everywhere. Validate (CNM / Clarke) lives in the same lab.
 */
import React, { useMemo, useState, useEffect, createElement } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { adminStyles as styles } from './adminStyles';
import * as betMath from '../utils/betMath';
import LabHeader from './LabHeader';
import MathValidationTab from './MathValidationTab';

const SECTIONS = [
  { key: 'calculator', label: 'Calculator', icon: 'calculator-outline' },
  { key: 'sgp', label: 'Parlay', icon: 'layers-outline' },
  { key: 'validate', label: 'Validate', icon: 'checkmark-done-outline' },
  { key: 'integrity', label: 'Integrity', icon: 'shield-checkmark-outline' },
  { key: 'howItWorks', label: 'How it works', icon: 'book-outline' },
  { key: 'limits', label: 'Limits', icon: 'options-outline' },
  { key: 'audit', label: 'Audit', icon: 'document-text-outline' },
];

const fmt = (v, digits = 2) =>
  (v === null || v === undefined || !Number.isFinite(v)) ? '—' : v.toFixed(digits);
const fmtPct = (v, digits = 2) =>
  (v === null || v === undefined || !Number.isFinite(v)) ? '—' : `${v.toFixed(digits)}%`;
const fmtOdds = (v) =>
  (v === null || v === undefined || !Number.isFinite(v)) ? '—' : (v > 0 ? `+${Math.round(v)}` : `${Math.round(v)}`);

const num = (s) => {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(String(s).replace(/[^0-9.+-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// ─── Small presentational helpers ────────────────────────────────────────────

function Card({ title, icon, subtitle, children, accent }) {
  return (
    <View style={[s.card, accent && { borderColor: 'rgba(56,189,248,0.3)' }]}>
      {title ? (
        <View style={s.cardHead}>
          {icon ? <Ionicons name={icon} size={15} color="#38bdf8" /> : null}
          <Text style={s.cardTitle}>{title}</Text>
        </View>
      ) : null}
      {subtitle ? <Text style={s.cardSub}>{subtitle}</Text> : null}
      {children}
    </View>
  );
}

function Field({ label, hint, value, onChangeText, placeholder }) {
  return (
    <View style={s.field}>
      <Text style={s.fieldLabel}>{label}</Text>
      <TextInput
        style={s.input}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor="rgba(255,255,255,0.28)"
        keyboardType="numbers-and-punctuation"
      />
      {hint ? <Text style={s.fieldHint}>{hint}</Text> : null}
    </View>
  );
}

function Row({ label, value, mono = true, tone }) {
  const toneColor = tone === 'ok' ? '#4ade80' : tone === 'bad' ? '#f87171' : tone === 'warn' ? '#fbbf24' : '#e8edf5';
  return (
    <View style={s.row}>
      <Text style={s.rowLabel}>{label}</Text>
      <Text style={[s.rowValue, mono && s.mono, { color: toneColor }]}>{value}</Text>
    </View>
  );
}

function Formula({ children, why }) {
  return (
    <View style={s.formula}>
      <Text style={[s.mono, s.formulaText]}>{children}</Text>
      {why ? <Text style={s.formulaWhy}>{why}</Text> : null}
    </View>
  );
}

function Callout({ tone = 'info', title, children }) {
  const map = {
    info: ['rgba(56,189,248,0.07)', 'rgba(56,189,248,0.28)', '#38bdf8'],
    ok: ['rgba(74,222,128,0.07)', 'rgba(74,222,128,0.28)', '#4ade80'],
    warn: ['rgba(251,191,36,0.07)', 'rgba(251,191,36,0.28)', '#fbbf24'],
    bad: ['rgba(248,113,113,0.07)', 'rgba(248,113,113,0.3)', '#f87171'],
  };
  const [bg, border, color] = map[tone] || map.info;
  return (
    <View style={[s.callout, { backgroundColor: bg, borderColor: border }]}>
      {title ? <Text style={[s.calloutTitle, { color }]}>{title}</Text> : null}
      <Text style={s.calloutBody}>{children}</Text>
    </View>
  );
}

// ─── Straight-bet calculator ─────────────────────────────────────────────────

function CalculatorSection() {
  const [american, setAmerican] = useState('-110');
  const [opposite, setOpposite] = useState('-110');
  const [refFair, setRefFair] = useState('');
  const [kelly, setKelly] = useState('0.25');

  const result = useMemo(() => {
    const a = num(american);
    if (a === null) return null;
    const refPct = num(refFair);
    return betMath.calculate({
      american: a,
      oppositeAmerican: num(opposite),
      referenceFairProb: refPct === null ? undefined : refPct / 100,
      kellyFraction: num(kelly) ?? 0.25,
    });
  }, [american, opposite, refFair, kelly]);

  const usingRef = result?.fairProbSource === 'reference_consensus';

  const evTone = (v) => (!Number.isFinite(v) ? undefined : v > 0.01 ? 'ok' : v < -0.01 ? 'bad' : undefined);

  return (
    <View>
      <Card title="Inputs" icon="create-outline"
        subtitle="Every value below feeds the same functions production uses. Leave a field blank to see the fallback behavior.">
        <View style={s.grid}>
          <Field label="Price (American)" value={american} onChangeText={setAmerican}
            placeholder="-110" hint="The odds you are evaluating" />
          <Field label="Opposite side (same book)" value={opposite} onChangeText={setOpposite}
            placeholder="-110" hint="Blank = fall back to assumed vig" />
          <Field label="Consensus fair %" value={refFair} onChangeText={setRefFair}
            placeholder="52.5" hint="Blank = self-devig (never +EV)" />
          <Field label="Kelly fraction" value={kelly} onChangeText={setKelly}
            placeholder="0.25" hint="0.25 = quarter Kelly" />
        </View>
      </Card>

      {!result ? (
        <Callout tone="warn" title="Enter a price">
          Type American odds above to see the full calculation.
        </Callout>
      ) : !result.valid ? (
        <Callout tone="bad" title="Invalid input">{result.error}</Callout>
      ) : (
        <>
          <Card title="Headline" icon="flash-outline" accent>
            <View style={s.headline}>
              <View style={s.headlineMain}>
                <Text style={s.headlineLabel}>Expected Value</Text>
                <Text style={[s.headlineValue, {
                  color: Number.isFinite(result.best.evPercent)
                    ? (result.best.evPercent > 0 ? '#4ade80' : result.best.evPercent < 0 ? '#f87171' : '#e8edf5')
                    : '#94a3b8',
                }]}>
                  {fmtPct(result.best.evPercent)}
                </Text>
                <Text style={s.headlineMeta}>
                  via {result.best.method || 'n/a'} · most conservative of three
                </Text>
              </View>
              <View style={s.headlineSide}>
                <Row label="Fair probability" value={fmtPct((result.best.fairProb ?? 0) * 100)} />
                <Row label="Fair price" value={fmtOdds(result.best.fairAmerican)} />
                <Row label="Kelly stake" value={fmtPct((result.kelly.scaled ?? 0) * 100)} />
              </View>
            </View>
          </Card>

          {result.fairProbSource === 'self_devig' ? (
            <Callout tone="warn" title="Self-devig — EV cannot be positive here">
              With no consensus reference, every method divides this book's implied probability by its
              own margin, so the result is always at or below the offered price. Enter a
              "Consensus fair %" to model a real edge. This is a structural property of the math, not
              a limitation of the calculator.
            </Callout>
          ) : (
            <Callout tone="ok" title="Priced against consensus">
              EV is computed against your external fair probability of {fmtPct(num(refFair) ?? 0, 2)},
              which is where genuine edge comes from.
            </Callout>
          )}

          <View style={s.twoCol}>
            <Card title="Step 1 — Price → probability" icon="swap-horizontal-outline">
              <Row label="American" value={fmtOdds(result.odds.american)} />
              <Row label="Decimal" value={fmt(result.odds.decimal, 4)} />
              <Row label="Implied probability" value={fmtPct(result.odds.impliedPercent, 3)} />
              <Row label="Payout ratio (Kelly b)" value={fmt(result.odds.payoutRatio, 4)} />
            </Card>

            <Card title="Step 2 — The vig" icon="cut-outline">
              <Row label="Total implied" value={fmt(result.vig.totalImplied, 5)} />
              <Row label="Vig" value={fmtPct(result.vig.vigPercent, 3)}
                tone={result.vig.vigPercent > 15 ? 'warn' : undefined} />
              <Row label="Source" value={result.vig.source} mono={false}
                tone={result.vig.source === 'same_book' ? 'ok' : result.vig.source === 'estimated' ? 'warn' : undefined} />
              <Row label="Opposite used" value={fmtOdds(result.vig.oppositeAmerican)} />
            </Card>
          </View>

          <Card title="Step 3 — Three devig methods" icon="git-branch-outline"
            subtitle={usingRef
              ? "These three rows devig this book against its own margin, so you can see how much the methods disagree. The number actually used is the consensus row below — that is the only source that can show a real edge."
              : "Three different assumptions about how the book spread its margin. We display the minimum, because disagreement between them is real uncertainty."}>
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 1.4 }]}>Method</Text>
              <Text style={[s.th, s.thNum]}>Fair prob</Text>
              <Text style={[s.th, s.thNum]}>Fair odds</Text>
              <Text style={[s.th, s.thNum]}>EV%</Text>
              <Text style={[s.th, s.thNum, { flex: 0.8 }]}>Used</Text>
            </View>
            {['additive', 'multiplicative', 'power'].map((m) => {
              // Always the self-devig figures. Under a consensus reference the
              // three entries in `fairProbs` collapse to one number, which would
              // render as three identical rows and hide the method spread.
              const fp = result.selfDevigProbs[m];
              const ev = result.selfDevigEvPercents[m];
              const isUsed = !usingRef && result.best.method === m;
              const gated = m === 'additive' && !result.additiveReliable;
              return (
                <View key={m} style={[s.tr, isUsed && s.trUsed]}>
                  <Text style={[s.td, { flex: 1.4 }, gated && s.tdGated]}>
                    {m}{gated ? ' (gated)' : ''}
                  </Text>
                  <Text style={[s.td, s.tdNum, s.mono]}>{fmtPct((fp ?? 0) * 100, 2)}</Text>
                  <Text style={[s.td, s.tdNum, s.mono]}>
                    {fp ? fmtOdds(betMath.impliedToAmerican(fp)) : '—'}
                  </Text>
                  <Text style={[s.td, s.tdNum, s.mono, {
                    color: Number.isFinite(ev) ? (ev > 0 ? '#4ade80' : ev < 0 ? '#f87171' : '#e8edf5') : '#94a3b8',
                  }]}>{fmtPct(ev)}</Text>
                  <Text style={[s.td, s.tdNum, { flex: 0.8, color: isUsed ? '#4ade80' : 'rgba(255,255,255,0.2)' }]}>
                    {isUsed ? '✓' : '—'}
                  </Text>
                </View>
              );
            })}

            {usingRef ? (
              <>
                <View style={[s.tr, s.trUsed, { borderTopWidth: 1, borderTopColor: 'rgba(56,189,248,0.35)' }]}>
                  <Text style={[s.td, { flex: 1.4, color: '#38bdf8', fontWeight: '700' }]}>consensus</Text>
                  <Text style={[s.td, s.tdNum, s.mono]}>
                    {fmtPct((result.referenceFairProb ?? 0) * 100, 2)}
                  </Text>
                  <Text style={[s.td, s.tdNum, s.mono]}>
                    {result.referenceFairProb
                      ? fmtOdds(betMath.impliedToAmerican(result.referenceFairProb))
                      : '—'}
                  </Text>
                  <Text style={[s.td, s.tdNum, s.mono, {
                    color: Number.isFinite(result.best.evPercent)
                      ? (result.best.evPercent > 0 ? '#4ade80' : result.best.evPercent < 0 ? '#f87171' : '#e8edf5')
                      : '#94a3b8',
                  }]}>{fmtPct(result.best.evPercent)}</Text>
                  <Text style={[s.td, s.tdNum, { flex: 0.8, color: '#4ade80' }]}>✓</Text>
                </View>
                <Text style={s.note}>
                  The consensus row is external to this book, which is why its EV can be positive.
                  Every row above is bounded at or below 0% by construction.
                </Text>
              </>
            ) : null}

            {!result.additiveReliable ? (
              <Text style={s.note}>
                Additive is gated out: implied probability is outside the 5–95% band where it behaves.
                Subtracting a flat margin from a near-zero probability produces nonsense.
              </Text>
            ) : null}
          </Card>

          <Card title="Step 4 — Stake sizing (Kelly)" icon="wallet-outline">
            <Row label="Full Kelly" value={fmtPct((result.kelly.full ?? 0) * 100)} />
            <Row label={`Scaled (${fmt(result.kelly.fraction, 2)}×)`} value={fmtPct((result.kelly.scaled ?? 0) * 100)} tone="ok" />
            <Text style={s.note}>
              Floored at 0 so a negative-EV bet is never sized, and capped at{' '}
              {fmtPct(betMath.LIMITS.KELLY_MAX_FRACTION * 100, 0)} of bankroll as a ruin guard.
            </Text>
          </Card>
        </>
      )}
    </View>
  );
}

// ─── Parlay calculator ───────────────────────────────────────────────────────

function SgpSection() {
  const [legA, setLegA] = useState('-110');
  const [legAOpp, setLegAOpp] = useState('-110');
  const [legB, setLegB] = useState('+150');
  const [legBOpp, setLegBOpp] = useState('-170');
  const [bookDecs, setBookDecs] = useState('4.5, 4.6, 4.4');
  const [offered, setOffered] = useState('+360');

  const build = (mathV2) => {
    const a = num(legA); const b = num(legB);
    if (a === null || b === null) return null;
    const decs = String(bookDecs).split(',').map((x) => Number(x.trim())).filter(Number.isFinite);
    return betMath.calculateSgp({
      legs: [
        { american: a, oppositeAmerican: num(legAOpp) },
        { american: b, oppositeAmerican: num(legBOpp) },
      ],
      bookSgpDecimals: decs,
      offeredAmerican: num(offered),
      mathV2,
    });
  };

  const v2 = useMemo(() => build(true), [legA, legAOpp, legB, legBOpp, bookDecs, offered]);
  const legacy = useMemo(() => build(false), [legA, legAOpp, legB, legBOpp, bookDecs, offered]);

  const delta = (v2?.valid && legacy?.valid && Number.isFinite(v2.best.evPercent) && Number.isFinite(legacy.best.evPercent))
    ? v2.best.evPercent - legacy.best.evPercent
    : null;

  return (
    <View>
      <Card title="Parlay legs" icon="layers-outline"
        subtitle="Same-game parlay legs are not independent. This runs both the corrected math and the legacy floored math side by side.">
        <View style={s.grid}>
          <Field label="Leg 1 price" value={legA} onChangeText={setLegA} placeholder="-110" />
          <Field label="Leg 1 opposite" value={legAOpp} onChangeText={setLegAOpp} placeholder="-110" />
          <Field label="Leg 2 price" value={legB} onChangeText={setLegB} placeholder="+150" />
          <Field label="Leg 2 opposite" value={legBOpp} onChangeText={setLegBOpp} placeholder="-170" />
        </View>
        <View style={s.grid}>
          <Field label="Book parlay decimals" value={bookDecs} onChangeText={setBookDecs}
            placeholder="4.5, 4.6, 4.4" hint="Comma-separated quotes across books" />
          <Field label="Offered parlay price" value={offered} onChangeText={setOffered}
            placeholder="+360" hint="The price being evaluated" />
        </View>
        <Text style={s.note}>
          Try book decimals far ABOVE the independence price (e.g. 62, 63, 62.5) to reproduce the
          Dort-class negative-correlation case that the legacy floor broke.
        </Text>
      </Card>

      {!v2?.valid ? (
        <Callout tone="warn" title="Enter two legs">{v2?.error || 'Both leg prices are required.'}</Callout>
      ) : (
        <>
          <Card title="Correlation (the R-factor)" icon="analytics-outline" accent>
            <Row label="Independence price (Π legs)" value={`${fmt(v2.independence.legsProductDecimal, 4)}  (${fmtOdds(v2.independence.legsProductAmerican)})`} />
            <Row label="Books' average parlay decimal" value={fmt(v2.correlation.avgBookDecimal, 4)} />
            <Row label="Raw R (market's estimate)" value={fmt(v2.correlation.rawR, 4)} tone="ok" />
            <Row label="Books in consensus" value={String(v2.correlation.bookCount)} />
            <Text style={s.note}>
              {!Number.isFinite(v2.correlation.rawR) ? 'Not enough book quotes to derive correlation.'
                : v2.correlation.rawR > 1.02
                  ? 'R > 1 — books price the parlay SHORTER than independence, meaning the legs are POSITIVELY correlated (they help each other).'
                  : v2.correlation.rawR < 0.98
                    ? 'R < 1 — books price the parlay LONGER than independence, meaning the legs are NEGATIVELY correlated (they fight each other).'
                    : 'R ≈ 1 — the market treats these legs as roughly independent.'}
            </Text>
          </Card>

          <View style={s.twoCol}>
            <Card title="Corrected (v2)" icon="checkmark-circle-outline">
              <Row label="R used" value={fmt(v2.correlation.r, 4)} tone="ok" />
              <Row label="R was clamped" value={v2.correlation.clamped ? 'yes' : 'no'} mono={false}
                tone={v2.correlation.clamped ? 'warn' : 'ok'} />
              <Row label="Correlated fair prob" value={fmtPct((v2.correlatedFairProbs[v2.best.method] ?? 0) * 100, 3)} />
              <Row label="EV" value={fmtPct(v2.best.evPercent)}
                tone={v2.best.evPercent > 0 ? 'ok' : 'bad'} />
            </Card>

            <Card title="Legacy (floored R)" icon="alert-circle-outline">
              <Row label="R used" value={fmt(legacy?.correlation?.r, 4)}
                tone={legacy?.correlation?.clamped ? 'bad' : undefined} />
              <Row label="R was clamped" value={legacy?.correlation?.clamped ? 'yes — floored' : 'no'} mono={false}
                tone={legacy?.correlation?.clamped ? 'bad' : 'ok'} />
              <Row label="Correlated fair prob" value={fmtPct((legacy?.correlatedFairProbs?.[legacy?.best?.method] ?? 0) * 100, 3)} />
              <Row label="EV" value={fmtPct(legacy?.best?.evPercent)}
                tone={legacy?.best?.evPercent > 0 ? 'warn' : undefined} />
            </Card>
          </View>

          {delta !== null && Math.abs(delta) > 0.05 ? (
            <Callout tone={delta < 0 ? 'bad' : 'info'} title={`Legacy math ${delta < 0 ? 'overstates' : 'understates'} EV by ${fmt(Math.abs(delta), 1)} pp`}>
              {delta < 0
                ? 'The legacy floor inflated the correlation factor, which inflated fair probability, which manufactured edge that does not exist. This is the bug the audit was written about.'
                : 'The legacy clamp moved EV in the conservative direction here.'}
            </Callout>
          ) : (
            <Callout tone="ok" title="Both policies agree on this parlay">
              R sits inside the legacy band, so the floor never engaged. This is the common case —
              measured at roughly 99% of real parlays.
            </Callout>
          )}

          <Card title="Per-method breakdown" icon="git-branch-outline">
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 1.4 }]}>Method</Text>
              <Text style={[s.th, s.thNum]}>Corrected EV</Text>
              <Text style={[s.th, s.thNum]}>Legacy EV</Text>
            </View>
            {['additive', 'multiplicative', 'power'].map((m) => (
              <View key={m} style={[s.tr, v2.best.method === m && s.trUsed]}>
                <Text style={[s.td, { flex: 1.4 }]}>{m}</Text>
                <Text style={[s.td, s.tdNum, s.mono]}>{fmtPct(v2.evPercents[m])}</Text>
                <Text style={[s.td, s.tdNum, s.mono]}>{fmtPct(legacy?.evPercents?.[m])}</Text>
              </View>
            ))}
          </Card>
        </>
      )}
    </View>
  );
}

// ─── Integrity check ─────────────────────────────────────────────────────────

function IntegritySection() {
  const [nonce, setNonce] = useState(0);
  const check = useMemo(() => betMath.selfCheck(), [nonce]);
  const passCount = check.checks.filter((c) => c.passed).length;

  return (
    <View>
      <Callout tone={check.passed ? 'ok' : 'bad'}
        title={check.passed ? `All ${check.checks.length} integrity assertions pass` : 'INTEGRITY FAILURE'}>
        {check.passed
          ? 'These assertions run live in your browser against the same module production uses. They prove the conversions round-trip, the devig methods redistribute exactly the full margin, a fair price yields exactly zero EV, and the R-factor bug is fixed.'
          : 'One or more core math assertions failed. Do not trust displayed EV until this is resolved.'}
      </Callout>

      <TouchableOpacity style={s.reRun} onPress={() => setNonce((n) => n + 1)} activeOpacity={0.85}>
        <Ionicons name="refresh-outline" size={14} color="#38bdf8" />
        <Text style={s.reRunText}>Re-run ({passCount}/{check.checks.length} passing)</Text>
      </TouchableOpacity>

      <Card title="Assertions" icon="shield-checkmark-outline">
        {check.checks.map((c, i) => (
          <View key={i} style={s.checkRow}>
            <View style={[s.checkDot, { backgroundColor: c.passed ? '#4ade80' : '#f87171' }]} />
            <View style={{ flex: 1 }}>
              <Text style={s.checkName}>{c.name}</Text>
              <Text style={[s.checkDetail, s.mono]}>{c.detail}</Text>
            </View>
            <Text style={[s.checkStatus, { color: c.passed ? '#4ade80' : '#f87171' }]}>
              {c.passed ? 'PASS' : 'FAIL'}
            </Text>
          </View>
        ))}
      </Card>

      <Callout tone="info" title="Why this panel exists">
        Verifying math by reading code does not scale, and trusting a single output number hides
        errors in the intermediate steps. These assertions check properties that must hold for any
        correct implementation — so if the math ever breaks, this panel goes red before a user sees a
        wrong number.
      </Callout>
    </View>
  );
}

// ─── How it works ────────────────────────────────────────────────────────────

function HowItWorksSection() {
  return (
    <View>
      <Callout tone="ok" title="Compute once, display everywhere">
        The backend runs all EV math at serve time (<Text style={s.mono}>annotateCleanEv</Text>) and stamps
        per-book <Text style={s.mono}>clean_ev_percent</Text> plus method rows on the wire. Cards read those
        stamps — they do not recompute unless a stamp is missing or you change consensus policy. The
        interactive calculators below call the same <Text style={s.mono}>betMath</Text> module the server uses.
      </Callout>

      <Callout tone="info" title="One file owns all of this">
        Authored at <Text style={s.mono}>functions/betMath.js</Text> and mirrored to{' '}
        <Text style={s.mono}>website/utils/betMath.js</Text> by <Text style={s.mono}>npm run math:sync</Text>.
        The pre-deploy gate fails if the two ever differ, so the frontend and backend cannot compute
        different numbers.
      </Callout>

      <Card title="Step 1 — Price becomes a probability" icon="swap-horizontal-outline">
        <Text style={s.body}>
          A sportsbook price is a probability in disguise. −110 means risk $110 to win $100, which
          implies a 52.38% chance. That figure includes the book's built-in margin.
        </Text>
        <Formula why="-110 → 0.5238    ·    +150 → 0.4000">
          implied = american &gt; 0 ? 100/(american+100) : |american|/(|american|+100)
        </Formula>
      </Card>

      <Card title="Step 2 — Remove the vig" icon="cut-outline">
        <Text style={s.body}>
          Both sides of a real market sum to more than 100%. A −110/−110 market sums to 104.76%; that
          extra 4.76% is the book's margin. We redistribute it to recover what the book actually
          thinks. There is no single correct way to do this, because we do not know how the book
          spread its margin — so we compute three methods and display the most conservative.
        </Text>
        <Formula why="Additive assumes a flat margin per outcome. Breaks on longshots, so it is gated to the 5–95% band.">
          additive: fair = p − (total − 1) / n
        </Formula>
        <Formula why="Multiplicative scales proportionally. The CNM standard.">
          multiplicative: fair = p / total
        </Formula>
        <Formula why="Power solves for the exponent that makes the devigged probabilities sum to exactly 1. Handles heavy favorites, where additive fails. Solved by binary search — no closed form.">
          power: solve Σ pᵢ^(1/k) = 1, then fair = p^(1/k)
        </Formula>
      </Card>

      <Card title="Step 3 — Compare against the market" icon="people-outline">
        <Callout tone="warn" title="The step most people get wrong">
          Devigging a single price can never produce positive EV. Every method divides a book's
          implied probability by that same book's margin, so the result is always at or below the
          offered price. You cannot beat a price using only that price.
        </Callout>
        <Text style={s.body}>
          A genuine edge exists only when one book's price is compared against a fair probability
          sourced from elsewhere — the consensus across many other books. Because one stale book can
          poison a small sample, the consensus estimator changes shape with the number of books:
          the value itself at n=1, the median at n=2–3, and a 20% trimmed mean at n≥4.
        </Text>
        <Formula why="One garbage book cannot move the consensus.">
          robustMean([1.90, 1.91, 1.92, 1.93, 50.0]) ≈ 1.92
        </Formula>
      </Card>

      <Card title="Step 4 — Expected value" icon="trending-up-outline">
        <Formula why="Stake 1 unit. Win decimalOdds back with probability fairProb. Subtract the stake. A 55% shot at even money → (0.55 × 2.00 − 1) × 100 = +10%.">
          EV% = (fairProb × decimalOdds − 1) × 100
        </Formula>
        <Text style={s.body}>
          Everything above exists only to make the fairProb input trustworthy. The formula itself was
          never the problem.
        </Text>
      </Card>

      <Card title="Extra step — parlay correlation" icon="layers-outline">
        <Text style={s.body}>
          Legs from the same game are not independent, so multiplying their probabilities is wrong in
          whichever direction they are correlated. Instead of modelling correlation ourselves, we
          read it out of the market: books quote the parlay directly, so the gap between the
          independence price and the quoted price is the market's own correlation estimate.
        </Text>
        <Formula why="Two 2.00 legs imply a 4.00 parlay (25%). Book quotes 3.00 (33%) → positively correlated → R = 1.33. Book quotes 8.00 (12.5%) → negatively correlated → R = 0.50.">
          R = legsProductDecimal / robustMean(bookParlayDecimals){'\n'}
          fairParlayProb = Π(legFairProbs) × R
        </Formula>
        <Callout tone="bad" title="This is where our worst bug lived">
          A hard floor of R ≥ 0.50 turned a genuine R of 0.076 into 0.50, inflating fair probability
          6.6× and manufacturing a +578% edge that did not exist. v2 math (now the production default)
          removes the floor and keeps only the 1.30 upper cap. Set{' '}
          <Text style={s.mono}>sgpMathV2Enabled: false</Text> in Firestore only to roll back.
        </Callout>
      </Card>

      <Card title="Finally — how much to bet" icon="wallet-outline">
        <Formula why="The stake that maximizes long-run bankroll growth. Full Kelly is correct but brutally volatile, so we scale it, floor it at zero, and cap it at 50% of bankroll.">
          f* = (b × p − q) / b     where b = decimal − 1, q = 1 − p
        </Formula>
      </Card>
    </View>
  );
}

// ─── Limits reference ────────────────────────────────────────────────────────

const LIMIT_NOTES = {
  PROB_MIN: 'Lower clamp on any fair probability — prevents division blowups downstream.',
  PROB_MAX: 'Upper clamp on any fair probability.',
  ADDITIVE_RELIABLE_MIN: 'Below this implied probability, additive devig is dropped from the min-set.',
  ADDITIVE_RELIABLE_MAX: 'Above this implied probability, additive devig is dropped from the min-set.',
  TOTAL_IMPLIED_DFS_FLOOR: 'Below this, the "market" is a DFS/flex-pick shape, not a real two-way market.',
  TOTAL_IMPLIED_MALFORMED_CEILING: 'Above this means mispaired lines or a triple-booked market.',
  ASSUMED_VIG_DEFAULT: 'Fallback margin when only one side of a market exists. Now calibrated per market type.',
  OPPOSITE_ODDS_MIN: 'Sentinel sanitize floor — the feed sends -99900 to mean "no opposing line".',
  OPPOSITE_ODDS_MAX: 'Sentinel sanitize ceiling.',
  POWER_K_LO: 'Power-devig binary search lower bound on k.',
  POWER_K_HI: 'Power-devig binary search upper bound on k.',
  POWER_ITERATIONS: 'Binary search iterations — converges far past double precision.',
  ROBUST_MEAN_TRIM: 'Fraction trimmed from each tail when 4 or more books are present.',
  R_FACTOR_MAX: 'Correlation ceiling. Truncates strong POSITIVE correlation — errs conservatively.',
  R_FACTOR_MIN_LEGACY: 'THE BUG. Legacy correlation floor that manufactured phantom edge. Removed in v2.',
  SGP_PROB_MIN: 'Lower clamp on a correlation-adjusted parlay probability.',
  SGP_PROB_MAX: 'Upper clamp on a correlation-adjusted parlay probability.',
  KELLY_MAX_FRACTION: 'Maximum fraction of bankroll for any single bet — ruin guard.',
};

function LimitsSection() {
  return (
    <View>
      <Callout tone="info" title="Roughly 30 magic numbers, now in one place">
        These were previously inline literals scattered across 12 files. Every one is now a named,
        documented constant in a single <Text style={s.mono}>LIMITS</Text> block, read live from the
        module below.
      </Callout>

      <Card title="LIMITS" icon="options-outline">
        <View style={s.tableHead}>
          <Text style={[s.th, { flex: 1.5 }]}>Constant</Text>
          <Text style={[s.th, s.thNum, { flex: 0.6 }]}>Value</Text>
          <Text style={[s.th, { flex: 2.6 }]}>Purpose</Text>
        </View>
        {Object.entries(betMath.LIMITS).map(([k, v]) => {
          const isBug = k === 'R_FACTOR_MIN_LEGACY';
          return (
            <View key={k} style={[s.tr, isBug && { backgroundColor: 'rgba(248,113,113,0.07)' }]}>
              <Text style={[s.td, s.mono, { flex: 1.5, fontSize: 11.5, color: isBug ? '#f87171' : '#e8edf5' }]}>{k}</Text>
              <Text style={[s.td, s.tdNum, s.mono, { flex: 0.6 }]}>{v}</Text>
              <Text style={[s.td, { flex: 2.6, color: 'rgba(255,255,255,0.55)', fontSize: 12 }]}>
                {LIMIT_NOTES[k] || '—'}
              </Text>
            </View>
          );
        })}
      </Card>

      <Callout tone="warn" title="Caps are not a substitute for correct math">
        A cap turns a wrong number into a less obviously wrong number. The +578% Dort parlay was
        displayed as +25% because a downstream cap clipped it — which made the symptom look plausible
        and delayed finding the real bug. Caps should catch data corruption, never compensate for
        math we do not trust.
      </Callout>
    </View>
  );
}

// ─── Audit report (generated MATH_AUDIT.html) ────────────────────────────────

function MetricPill({ label, value, tone }) {
  const color = tone === 'ok' ? '#4ade80' : tone === 'bad' ? '#f87171' : tone === 'warn' ? '#fbbf24' : '#38bdf8';
  return (
    <View style={s.metricPill}>
      <Text style={[s.metricValue, { color }]}>{value}</Text>
      <Text style={s.metricLabel}>{label}</Text>
    </View>
  );
}

function AuditReportSection() {
  const [meta, setMeta] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeHash, setIframeHash] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/math-audit-meta.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (!cancelled) setMeta(data); })
      .catch((e) => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const reportHref = Platform.OS === 'web' && typeof window !== 'undefined'
    ? `${window.location.origin}/math-audit.html`
    : null;

  const openReport = () => {
    if (reportHref) window.open(reportHref, '_blank', 'noopener,noreferrer');
  };

  const trialHref = meta?.trial_guide?.section_url && Platform.OS === 'web' && typeof window !== 'undefined'
    ? `${window.location.origin}${meta.trial_guide.section_url}`
    : reportHref ? `${reportHref}#trial` : null;

  const openTrialGuide = () => {
    if (trialHref) window.open(trialHref, '_blank', 'noopener,noreferrer');
  };

  const scrollTrialInEmbed = () => {
    if (Platform.OS !== 'web' || !reportHref) return;
    setIframeHash('#trial');
    setIframeKey((k) => k + 1);
  };

  if (loadError && !meta) {
    return (
      <Card title="Math Audit Report" icon="document-text-outline" accent>
        <Callout tone="warn" title="Audit report unavailable">
          Could not load <Text style={s.mono}>/math-audit-meta.json</Text>
          {loadError ? ` (${loadError})` : ''}.
          {'\n'}From repo root run <Text style={s.mono}>npm run math:audit</Text>, then rebuild web with{' '}
          <Text style={s.mono}>npm run postbuild:web</Text> in <Text style={s.mono}>website/</Text> and redeploy hosting.
        </Callout>
      </Card>
    );
  }

  if (!meta) {
    return (
      <Card title="Math Audit Report" icon="document-text-outline">
        <ActivityIndicator color="#38bdf8" />
        <Text style={[s.cardSub, { marginTop: 8 }]}>Loading audit metadata…</Text>
      </Card>
    );
  }

  const pending = meta.task_counts?.pending ?? 0;
  const inProg = meta.task_counts?.in_progress ?? 0;

  return (
    <View>
      <Card title="Math Audit Report" icon="document-text-outline" accent
        subtitle="Client-ready math audit — compute once on the backend, display via stamps. Regenerate with npm run math:audit before sharing.">
        <View style={s.metricRow}>
          <MetricPill label="Parlays sampled" value={meta.sample?.sgps?.toLocaleString?.() ?? '—'} />
          <MetricPill label="Stamp-first" value="✓" tone="ok" />
          <MetricPill label="SGP math v2" value={meta.metrics?.sgp_math_v2_default ? 'ON' : 'OFF'} tone={meta.metrics?.sgp_math_v2_default ? 'ok' : 'warn'} />
          <MetricPill label="Mirror parity" value={meta.integrity?.mirror_parity_ok ? '✓' : '✗'} tone={meta.integrity?.mirror_parity_ok ? 'ok' : 'bad'} />
        </View>
        <View style={[s.metricRow, { marginTop: 8 }]}>
          <MetricPill label="min(clean,ev)" value={meta.metrics?.min_clean_binding_pct != null ? `${meta.metrics.min_clean_binding_pct}%` : '—'} tone="warn" />
          <MetricPill label="Pill ≠ stored" value={meta.metrics?.pill_divergent_pct != null ? `${meta.metrics.pill_divergent_pct}%` : '—'} tone="warn" />
          <MetricPill label="R @ 1.30" value={meta.metrics?.r_ceiling_pct != null ? `${meta.metrics.r_ceiling_pct}%` : '—'} />
          <MetricPill label="Prop cap 35%" value={meta.metrics?.prop_cap_pct != null ? `${meta.metrics.prop_cap_pct}%` : '—'} />
        </View>
        <Row label="Generated" value={meta.generated_at ? new Date(meta.generated_at).toLocaleString() : '—'} mono={false} />
        <Row label="App version" value={`v${meta.version || '?'}`} />
        <Row label="Math tests" value={meta.integrity?.tests_passed != null ? `${meta.integrity.tests_passed}/${meta.integrity.tests_total}` : '—'} />
        <Row label="Open tasks" value={`${pending} pending · ${inProg} in progress`} mono={false} />

        {Platform.OS === 'web' ? (
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <TouchableOpacity style={s.btn} onPress={openReport} activeOpacity={0.85}>
              <Ionicons name="open-outline" size={14} color="#38bdf8" />
              <Text style={s.btnText}>Open full report (new tab)</Text>
            </TouchableOpacity>
            {meta.trial_guide ? (
              <TouchableOpacity style={[s.btn, s.btnTrial]} onPress={openTrialGuide} activeOpacity={0.85}>
                <Ionicons name="school-outline" size={14} color="#a78bfa" />
                <Text style={[s.btnText, { color: '#a78bfa' }]}>Developer trial guide</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity style={[s.btn, s.btnGhost]} onPress={() => setIframeKey((k) => k + 1)} activeOpacity={0.85}>
              <Ionicons name="refresh-outline" size={14} color="rgba(255,255,255,0.6)" />
              <Text style={[s.btnText, { color: 'rgba(255,255,255,0.6)' }]}>Reload embed</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </Card>

      {meta.trial_guide ? (
        <Card title="Developer trial onboarding" icon="school-outline" accent
          subtitle="Share with candidates before they touch the codebase — scaffold a calculator using betMath, then open a PR.">
          <Callout tone="info" title="Included in the math audit">
            The full brief lives in section 12 of the report below ({meta.trial_guide.source}).
            Send them <Text style={s.mono}>/math-audit.html#trial</Text> or use the button above.
          </Callout>
          <Text style={s.cardSub}>
            Task: wire inputs → <Text style={s.mono}>betMath.calculate()</Text> → readable output.
            Reference implementation: Math Lab → Calculator. No reimplemented formulas.
          </Text>
          {Platform.OS === 'web' ? (
            <TouchableOpacity style={[s.btn, s.btnTrial, { marginTop: 10, alignSelf: 'flex-start' }]} onPress={scrollTrialInEmbed} activeOpacity={0.85}>
              <Ionicons name="arrow-down-outline" size={14} color="#a78bfa" />
              <Text style={[s.btnText, { color: '#a78bfa' }]}>Jump to trial section in embed</Text>
            </TouchableOpacity>
          ) : null}
        </Card>
      ) : null}

      {meta.phases?.length ? (
        <Card title="Roadmap phases" icon="map-outline">
          {meta.phases.map((p) => (
            <View key={p.id} style={s.phaseRow}>
              <Text style={s.phaseTitle}>P{p.id} — {p.title}</Text>
              <Text style={s.phaseMeta}>{p.status?.replace('_', ' ')} · {p.taskDone}/{p.taskTotal} tasks</Text>
            </View>
          ))}
        </Card>
      ) : null}

      {meta.tasks?.length ? (
        <Card title="Remaining tasks" icon="list-outline" subtitle="Full detail in the embedded report below.">
          {meta.tasks.filter((t) => t.status !== 'complete').slice(0, 8).map((t) => (
            <View key={t.id} style={s.taskRow}>
              <Text style={s.taskTitle}>[{t.priority?.toUpperCase()}] {t.title}</Text>
              <Text style={s.taskMeta}>Phase {t.phase} · {t.status}</Text>
            </View>
          ))}
          {meta.tasks.filter((t) => t.status !== 'complete').length > 8 ? (
            <Text style={s.cardSub}>+ {meta.tasks.filter((t) => t.status !== 'complete').length - 8} more in full report</Text>
          ) : null}
        </Card>
      ) : null}

      {Platform.OS === 'web' && reportHref ? (
        <Card title="Full report" icon="reader-outline" subtitle="Scroll inside the frame — links jump to sections.">
          {createElement('iframe', {
            key: iframeKey,
            title: 'Math audit report',
            src: `${reportHref}?v=${iframeKey}${iframeHash}`,
            style: {
              width: '100%',
              height: 720,
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              backgroundColor: '#0b0f17',
            },
          })}
        </Card>
      ) : (
        <Callout tone="info">Full HTML report is available in the web admin (Admin → Labs → Math Lab → Audit).</Callout>
      )}
    </View>
  );
}

// ─── Tab shell ───────────────────────────────────────────────────────────────

export default function MathCoreTab({ initialSection = 'calculator', onSectionChange }) {
  const [section, setSection] = useState(initialSection);

  useEffect(() => {
    if (initialSection && initialSection !== section) setSection(initialSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only follow parent hash, not local clicks
  }, [initialSection]);

  const selectSection = (key) => {
    setSection(key);
    onSectionChange?.(key);
  };

  return (
    <View style={styles.tabWrap}>
      <LabHeader
        icon="calculator-outline"
        iconColor="#38bdf8"
        title="Math Lab"
        question="Is this EV / fair / Kelly number the one production uses?"
        how="Calculator and Parlay call betMath directly. Validate proves those formulas against CNM and Clarke. Integrity / Limits / Audit are the spec."
      />

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.navScroll}>
        <View style={s.nav}>
          {SECTIONS.map((sec) => {
            const active = section === sec.key;
            return (
              <TouchableOpacity
                key={sec.key}
                onPress={() => selectSection(sec.key)}
                activeOpacity={0.85}
                style={[s.navItem, active && s.navItemActive]}
              >
                <Ionicons name={sec.icon} size={14} color={active ? '#38bdf8' : 'rgba(255,255,255,0.45)'} />
                <Text style={[s.navText, active && s.navTextActive]}>{sec.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      <ScrollView style={s.bodyScroll} contentContainerStyle={s.bodyScrollContent} showsVerticalScrollIndicator={false}>
      {section === 'calculator' && <CalculatorSection />}
      {section === 'sgp' && <SgpSection />}
      {section === 'validate' && <MathValidationTab embedded />}
      {section === 'integrity' && <IntegritySection />}
      {section === 'howItWorks' && <HowItWorksSection />}
      {section === 'limits' && <LimitsSection />}
      {section === 'audit' && <AuditReportSection />}

      <View style={s.footer}>
        <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.3)" />
        <Text style={s.footerText}>
          Source: <Text style={s.mono}>functions/betMath.js</Text> · Mirror:{' '}
          <Text style={s.mono}>website/utils/betMath.js</Text> · Regenerate audit:{' '}
          <Text style={s.mono}>npm run math:audit</Text> · Plan:{' '}
          <Text style={s.mono}>scripts/mathMasterPlan.mjs</Text>
        </Text>
      </View>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  mono: { fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' },

  navScroll: { marginTop: 12, marginBottom: 4, flexGrow: 0 },
  bodyScroll: { flex: 1 },
  bodyScrollContent: { paddingBottom: 28 },
  nav: { flexDirection: 'row', gap: 6, paddingBottom: 8 },
  navItem: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 13, paddingVertical: 8, borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
  },
  navItemActive: { backgroundColor: 'rgba(56,189,248,0.1)', borderColor: 'rgba(56,189,248,0.35)' },
  navText: { color: 'rgba(255,255,255,0.5)', fontSize: 12.5, fontWeight: '600' },
  navTextActive: { color: '#38bdf8' },

  card: {
    backgroundColor: 'rgba(255,255,255,0.035)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 14, padding: 16, marginTop: 12,
  },
  cardHead: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 4 },
  cardTitle: { color: '#fff', fontSize: 14.5, fontWeight: '700', letterSpacing: -0.2 },
  cardSub: { color: 'rgba(255,255,255,0.42)', fontSize: 12.5, marginBottom: 10, lineHeight: 18 },

  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  field: { flexGrow: 1, flexBasis: Platform.OS === 'web' ? 'calc(50% - 6px)' : '100%', minWidth: 150 },
  fieldLabel: { color: 'rgba(255,255,255,0.6)', fontSize: 11.5, fontWeight: '600', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 },
  input: {
    backgroundColor: 'rgba(0,0,0,0.3)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 8, paddingHorizontal: 11, paddingVertical: 9, color: '#fff', fontSize: 14,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
  },
  fieldHint: { color: 'rgba(255,255,255,0.28)', fontSize: 11, marginTop: 4 },

  row: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  rowLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 12.5, flex: 1 },
  rowValue: { fontSize: 13, fontWeight: '600', textAlign: 'right' },

  headline: { flexDirection: 'row', flexWrap: 'wrap', gap: 18, alignItems: 'center' },
  headlineMain: { flexGrow: 1, minWidth: 180 },
  headlineLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7 },
  headlineValue: { fontSize: 40, fontWeight: '800', letterSpacing: -1.4, marginVertical: 2 },
  headlineMeta: { color: 'rgba(255,255,255,0.35)', fontSize: 11.5 },
  headlineSide: { flexGrow: 1, minWidth: 200 },

  twoCol: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },

  tableHead: {
    flexDirection: 'row', paddingBottom: 7, marginBottom: 2,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  th: { color: 'rgba(255,255,255,0.35)', fontSize: 10.5, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 },
  thNum: { textAlign: 'right' },
  tr: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 7,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)',
  },
  trUsed: { backgroundColor: 'rgba(74,222,128,0.06)' },
  td: { color: 'rgba(255,255,255,0.8)', fontSize: 12.5, flex: 1 },
  tdNum: { textAlign: 'right' },
  tdGated: { color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },

  note: { color: 'rgba(255,255,255,0.35)', fontSize: 11.5, lineHeight: 17, marginTop: 9, fontStyle: 'italic' },
  body: { color: 'rgba(255,255,255,0.68)', fontSize: 13.5, lineHeight: 21, marginBottom: 4 },

  formula: {
    backgroundColor: 'rgba(56,189,248,0.06)', borderWidth: 1, borderColor: 'rgba(56,189,248,0.2)',
    borderLeftWidth: 3, borderLeftColor: '#38bdf8',
    borderRadius: 9, padding: 13, marginVertical: 9,
  },
  formulaText: { color: '#e0f2fe', fontSize: 13, lineHeight: 20 },
  formulaWhy: { color: 'rgba(255,255,255,0.45)', fontSize: 12, lineHeight: 18, marginTop: 7 },

  callout: { borderWidth: 1, borderRadius: 10, padding: 13, marginTop: 12 },
  calloutTitle: { fontSize: 13, fontWeight: '700', marginBottom: 4 },
  calloutBody: { color: 'rgba(255,255,255,0.6)', fontSize: 12.5, lineHeight: 19 },

  reRun: {
    flexDirection: 'row', alignItems: 'center', gap: 7, alignSelf: 'flex-start',
    marginTop: 12, paddingHorizontal: 13, paddingVertical: 8, borderRadius: 8,
    backgroundColor: 'rgba(56,189,248,0.1)', borderWidth: 1, borderColor: 'rgba(56,189,248,0.3)',
  },
  reRunText: { color: '#38bdf8', fontSize: 12.5, fontWeight: '600' },

  checkRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)',
  },
  checkDot: { width: 7, height: 7, borderRadius: 4 },
  checkName: { color: 'rgba(255,255,255,0.85)', fontSize: 12.5, fontWeight: '600' },
  checkDetail: { color: 'rgba(255,255,255,0.32)', fontSize: 11, marginTop: 2 },
  checkStatus: { fontSize: 10.5, fontWeight: '800', letterSpacing: 0.5 },

  footer: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginTop: 20, padding: 12,
    backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 8,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)',
  },
  footerText: { flex: 1, color: 'rgba(255,255,255,0.3)', fontSize: 11, lineHeight: 17 },

  metricRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginVertical: 10 },
  metricPill: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, paddingVertical: 10, paddingHorizontal: 14,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', minWidth: 110,
  },
  metricValue: { fontSize: 20, fontWeight: '800', letterSpacing: -0.5 },
  metricLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10.5, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.4 },

  btn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 8, backgroundColor: 'rgba(56,189,248,0.12)', borderWidth: 1, borderColor: 'rgba(56,189,248,0.35)',
  },
  btnGhost: { backgroundColor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' },
  btnTrial: { backgroundColor: 'rgba(167,139,250,0.1)', borderColor: 'rgba(167,139,250,0.35)' },
  btnText: { color: '#38bdf8', fontSize: 12.5, fontWeight: '600' },

  phaseRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)' },
  phaseTitle: { color: '#e8edf5', fontSize: 13, fontWeight: '600' },
  phaseMeta: { color: 'rgba(255,255,255,0.4)', fontSize: 11.5, marginTop: 2 },

  taskRow: { paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)' },
  taskTitle: { color: 'rgba(255,255,255,0.85)', fontSize: 12.5, lineHeight: 18 },
  taskMeta: { color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2 },
});
